/**
 * Staff Inspiration Wall — displays a rotating deck of published,
 * admin-approved motivational quotes with a per-user heart reaction and
 * a "Submit a quote" CTA available to every authenticated user.
 *
 * Product rules:
 *   - Approved quotes rotate every 12 seconds (auto-advance).
 *   - Heart is one-per-user (toggle). Hover the count to see who ❤.
 *   - Rejected/pending quotes never appear here; approval happens in the
 *     Setup → Quote moderation panel (super_user + admin).
 */
import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Heart, Quote as QuoteIcon, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/AuthContext';

const ROTATE_MS = 12 * 1000;

export default function QuoteWall() {
  const { user } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [idx, setIdx] = useState(0);
  const [openSubmit, setOpenSubmit] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/quotes/rotation?limit=50');
      setQuotes(r.data?.quotes || []);
    } catch {
      setQuotes([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (quotes.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % quotes.length), ROTATE_MS);
    return () => clearInterval(t);
  }, [quotes.length]);

  const current = quotes[idx];

  const advance = (dir) => {
    if (!quotes.length) return;
    setIdx(i => (i + dir + quotes.length) % quotes.length);
  };

  const toggleHeart = async () => {
    if (!current) return;
    // Optimistic
    const wasHearted = current.viewer_has_hearted;
    setQuotes(qs => qs.map(q => q.quote_id === current.quote_id ? {
      ...q,
      viewer_has_hearted: !wasHearted,
      heart_count: q.heart_count + (wasHearted ? -1 : 1),
      hearted_by: wasHearted
        ? (q.hearted_by || []).filter(h => (h.email || '').toLowerCase() !== (user?.email || '').toLowerCase())
        : [...(q.hearted_by || []), { email: user?.email, name: user?.name || user?.email?.split('@')[0] || 'You', at: new Date().toISOString() }],
    } : q));
    try {
      const r = await api.post(`/quotes/${current.quote_id}/heart`);
      const upd = r.data;
      setQuotes(qs => qs.map(q => q.quote_id === current.quote_id ? { ...q, ...upd } : q));
    } catch {
      // Roll back on failure
      setQuotes(qs => qs.map(q => q.quote_id === current.quote_id ? {
        ...q,
        viewer_has_hearted: wasHearted,
        heart_count: q.heart_count + (wasHearted ? 1 : -1),
      } : q));
      toast.error('Could not save your heart — please try again.');
    }
  };

  return (
    <Card
      className="rounded-2xl border-[#E8E5EC] p-6 relative overflow-hidden bg-gradient-to-br from-white via-[#FAF8FD] to-[#F5F0FB]"
      data-testid="quote-wall"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-serif text-2xl text-[#2D2833] flex items-center gap-2">
            <QuoteIcon size={18} className="text-[#BAA9CA]" />
            Staff Inspiration Wall
          </h3>
          <p className="text-xs text-[#685B72] mt-0.5">
            Every quote your team has ever shared — rotating one at a time.
          </p>
        </div>
      </div>

      {current ? (
        <div className="min-h-[140px] flex flex-col justify-between" data-testid={`quote-card-${current.quote_id}`}>
          <blockquote className="text-lg font-serif text-[#2D2833] leading-relaxed pl-3 border-l-4 border-[#BAA9CA]/50">
            <span className="text-[#BAA9CA] text-2xl leading-none mr-1">“</span>
            {current.text}
            <span className="text-[#BAA9CA] text-2xl leading-none ml-1">”</span>
          </blockquote>
          <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-sm text-[#685B72]" data-testid="quote-author">— {current.author_name}</div>
              <Dialog open={openSubmit} onOpenChange={setOpenSubmit}>
                <DialogTrigger asChild>
                  <button
                    className="inline-flex items-center gap-1 text-[11px] text-[#685B72] hover:text-[#2D2833] hover:underline"
                    data-testid="quote-submit-btn"
                    title="Add a new quote to the wall"
                  >
                    <Plus size={11} /> Add quote
                  </button>
                </DialogTrigger>
                <QuoteSubmitDialog onDone={() => { setOpenSubmit(false); load(); }} />
              </Dialog>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={toggleHeart}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  current.viewer_has_hearted
                    ? 'bg-[#8A3A3A]/15 border-[#8A3A3A]/40 text-[#8A3A3A]'
                    : 'bg-white border-[#E8E5EC] text-[#685B72] hover:border-[#8A3A3A]/40 hover:text-[#8A3A3A]'
                }`}
                title={current.hearted_by?.length ? `Hearted by: ${current.hearted_by.map(h => h.name || h.email).join(', ')}` : 'Be the first to ❤'}
                data-testid={`quote-heart-${current.quote_id}`}
              >
                <Heart
                  size={13}
                  fill={current.viewer_has_hearted ? '#8A3A3A' : 'none'}
                  strokeWidth={2}
                />
                {current.heart_count || 0}
              </button>
              {quotes.length > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => advance(-1)}
                    className="w-7 h-7 rounded-full border border-[#E8E5EC] flex items-center justify-center text-[#685B72] hover:bg-white"
                    aria-label="Previous quote"
                    data-testid="quote-prev"
                  >
                    <ChevronLeft size={14} />
                  </button>
                  <span className="text-[10px] text-[#685B72] tabular-nums">
                    {idx + 1} / {quotes.length}
                  </span>
                  <button
                    onClick={() => advance(1)}
                    className="w-7 h-7 rounded-full border border-[#E8E5EC] flex items-center justify-center text-[#685B72] hover:bg-white"
                    aria-label="Next quote"
                    data-testid="quote-next"
                  >
                    <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="py-6" data-testid="quote-empty">
          <div className="text-sm text-[#685B72] italic mb-2">No quotes on the wall yet — be the first to share one.</div>
          <Dialog open={openSubmit} onOpenChange={setOpenSubmit}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="rounded-full text-xs border-[#BAA9CA]/60 text-[#685B72] hover:bg-[#BAA9CA]/10"
                data-testid="quote-submit-btn-empty"
              >
                <Plus size={13} className="mr-1" /> Add the first quote
              </Button>
            </DialogTrigger>
            <QuoteSubmitDialog onDone={() => { setOpenSubmit(false); load(); }} />
          </Dialog>
        </div>
      )}
    </Card>
  );
}

export function QuoteSubmitDialog({ onDone, editingQuote }) {
  const { user } = useAuth();
  const isSuper = user?.role === 'super_user';
  const isEditing = !!editingQuote;
  const [text, setText] = useState('');
  const [authorName, setAuthorName] = useState(user?.name || user?.email?.split('@')[0] || '');
  const [expiresOn, setExpiresOn] = useState('');
  const [busy, setBusy] = useState(false);

  // Prefill when opening for edit
  useEffect(() => {
    if (editingQuote) {
      setText(editingQuote.text || '');
      setAuthorName(editingQuote.author_name || '');
      setExpiresOn(editingQuote.expires_at ? editingQuote.expires_at.slice(0, 10) : '');
    } else {
      setText('');
      setAuthorName(user?.name || user?.email?.split('@')[0] || '');
      setExpiresOn('');
    }
  }, [editingQuote, user]);

  const submit = async () => {
    const t = text.trim();
    if (t.length < 6) {
      toast.error('Please share at least a few words.');
      return;
    }
    if (t.length > 500) {
      toast.error('Please keep it under 500 characters.');
      return;
    }
    setBusy(true);
    try {
      if (isEditing) {
        await api.patch(`/quotes/${editingQuote.quote_id}`, {
          text: t,
          author_name: authorName.trim() || undefined,
          expires_at: expiresOn || null,
        });
        toast.success('Quote updated.');
      } else {
        const payload = { text: t, author_name: authorName.trim() || undefined };
        if (expiresOn) payload.expires_at = expiresOn;
        await api.post('/quotes', payload);
        toast.success(isSuper
          ? (expiresOn
              ? `Published — this quote will retire after ${expiresOn}.`
              : 'Published — your quote is now on the wall.')
          : 'Thanks — an admin will review your quote and add it to the wall.');
      }
      setText('');
      setExpiresOn('');
      onDone?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || (isEditing ? 'Could not update the quote' : 'Could not save your quote');
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-md" data-testid="quote-submit-dialog">
      <DialogHeader>
        <DialogTitle className="font-serif text-2xl text-[#2D2833]">
          {isEditing ? 'Edit quote' : 'Share a quote'}
        </DialogTitle>
      </DialogHeader>
      <p className="text-sm text-[#685B72] leading-relaxed">
        {isEditing
          ? 'Fix typos, refresh wording, or update the expiry date. Changes save immediately.'
          : (isSuper
              ? 'Words that lifted you, a mantra you tell yourself, or something a family said that stuck with you. As a super user, your quotes publish instantly.'
              : 'Words that lifted you, a mantra you tell yourself, or something a family said that stuck with you. Anything approved becomes part of the wall forever.')
        }
      </p>
      <div className="space-y-3 mt-2">
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#685B72] mb-1">Quote</div>
          <Textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={5}
            placeholder="What's the quote?"
            className="rounded-xl border-[#E8E5EC]"
            maxLength={500}
            data-testid="quote-submit-text"
          />
          <div className="text-[10px] text-[#685B72] mt-1 text-right">{text.length}/500</div>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#685B72] mb-1">Author (display name)</div>
          <Input
            value={authorName}
            onChange={e => setAuthorName(e.target.value)}
            placeholder="How you'd like to be credited"
            className="rounded-xl border-[#E8E5EC]"
            data-testid="quote-submit-author"
          />
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#685B72] mb-1 flex items-center gap-1">
            Expires On
            <span className="text-[9px] normal-case text-[#685B72] font-normal">— optional, for seasonal quotes</span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={expiresOn}
              onChange={e => setExpiresOn(e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              className="rounded-xl border-[#E8E5EC]"
              data-testid="quote-submit-expires"
            />
            {expiresOn && (
              <button
                type="button"
                onClick={() => setExpiresOn('')}
                className="text-[10px] text-[#685B72] hover:underline"
                data-testid="quote-submit-expires-clear"
              >Clear</button>
            )}
          </div>
          <div className="text-[10px] text-[#685B72] mt-1">
            {expiresOn
              ? `Will stop rotating after end of day ${expiresOn}.`
              : 'Leave blank for a permanent quote.'}
          </div>
        </label>
      </div>
      <DialogFooter>
        <Button
          disabled={busy || !text.trim()}
          onClick={submit}
          className="bg-[#2D2833] text-white rounded-xl hover:bg-[#685B72]"
          data-testid="quote-submit-save"
        >
          {busy
            ? (isEditing ? 'Saving…' : (isSuper ? 'Publishing…' : 'Sending…'))
            : (isEditing ? 'Save changes' : (isSuper ? 'Publish quote' : 'Send for approval'))
          }
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
